/**
 * Reschedule page — Customer self-service booking reschedule.
 *
 * URL: #/reschedule/:bookingId?token=:token
 *
 * Flow:
 *   1. Validates token via API
 *   2. Shows current booking details
 *   3. Shows available alternative slots (reuses slot picker pattern)
 *   4. On slot selection, calls POST /v1/bookings/:id/reschedule
 *   5. Shows confirmation message
 */
import {
  searchAvailability,
  type AvailabilityByDate,
  type AvailableSlot,
  type BookingStatus,
} from '../api.js';

// ── Types ─────────────────────────────────────────────────────────────

interface RescheduleParams {
  bookingId: string;
  token: string;
}

// ── State ─────────────────────────────────────────────────────────────

let currentWeekStart = new Date();
let selectedDate: string | null = null;
let availability: AvailabilityByDate[] = [];

// ── Helpers ───────────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-IE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function formatTime(isoStr: string): string {
  const date = new Date(isoStr);
  return date.toLocaleTimeString('en-IE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatDateTime(isoStr: string): string {
  try {
    return new Date(isoStr).toLocaleString('en-IE', {
      timeZone: 'Europe/Dublin',
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return isoStr;
  }
}

function getWeekDates(startDate: Date): string[] {
  const dates: string[] = [];
  const current = new Date(startDate);
  const day = current.getDay();
  const diff = current.getDate() - day + (day === 0 ? -6 : 1);
  current.setDate(diff);
  for (let i = 0; i < 7; i++) {
    dates.push(current.toISOString().slice(0, 10));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

function getApiBase(): string {
  if (window.__TIMESLOT_CONFIG__?.apiBase) {
    return window.__TIMESLOT_CONFIG__.apiBase;
  }
  const match = window.location.pathname.match(/^\/book\/([a-z0-9-]+)/);
  if (match?.[1]) {
    return `/v1/public/${match[1]}`;
  }
  return '/v1/public/unknown';
}

// ── API ───────────────────────────────────────────────────────────────

async function fetchBookingForReschedule(bookingId: string, token: string): Promise<BookingStatus> {
  const response = await fetch(`${getApiBase()}/bookings/${encodeURIComponent(bookingId)}/reschedule-info?token=${encodeURIComponent(token)}`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: 'Failed to load booking' }));
    throw new Error((err as { message: string }).message ?? 'Failed to load booking');
  }
  const body = await response.json() as { data: BookingStatus };
  return body.data;
}

async function submitReschedule(bookingId: string, token: string, newStartTime: string): Promise<void> {
  const response = await fetch(`/v1/bookings/${encodeURIComponent(bookingId)}/reschedule`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ token, new_start_time: newStartTime }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: 'Reschedule failed' }));
    throw new Error((err as { message: string }).message ?? 'Reschedule failed');
  }
}

// ── Date Selector ─────────────────────────────────────────────────────

function renderDateSelector(weekDates: string[]): string {
  const today = new Date().toISOString().slice(0, 10);
  const dateButtons = weekDates.map((date) => {
    const isSelected = date === selectedDate;
    const isPast = date < today;
    const hasSlots = availability.some((d) => d.date === date && d.slots.length > 0);
    const d = new Date(date + 'T00:00:00');
    const dayName = d.toLocaleDateString('en-IE', { weekday: 'short' });
    const dayNum = d.getDate();
    return `
      <button
        class="ts-date-btn ${isSelected ? 'ts-date-btn--selected' : ''} ${isPast ? 'ts-date-btn--disabled' : ''} ${hasSlots ? 'ts-date-btn--available' : ''}"
        data-action="select-date" data-date="${date}"
        ${isPast ? 'disabled' : ''}
        aria-label="${formatDate(date)}${hasSlots ? ', slots available' : ', no slots'}"
        role="option" aria-selected="${isSelected}">
        <span class="ts-date-btn__day">${dayName}</span>
        <span class="ts-date-btn__num">${dayNum}</span>
        ${hasSlots ? '<span class="ts-date-btn__dot" aria-hidden="true"></span>' : ''}
      </button>`;
  }).join('');

  return `
    <div class="ts-date-nav" role="group" aria-label="Date selection">
      <button class="ts-date-nav__arrow ts-date-nav__prev" data-action="prev-week" aria-label="Previous week">
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M12 4l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <div class="ts-date-nav__dates" role="listbox" aria-label="Available dates">
        ${dateButtons}
      </div>
      <button class="ts-date-nav__arrow ts-date-nav__next" data-action="next-week" aria-label="Next week">
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M8 4l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>`;
}

// ── Slot Grid ─────────────────────────────────────────────────────────

function renderSlotGrid(slots: AvailableSlot[]): string {
  if (slots.length === 0) {
    return `
      <div class="ts-empty-state" role="status">
        <p class="ts-empty-state__title">No slots available</p>
        <p class="ts-empty-state__message">Try the next week or pick a different date.</p>
      </div>`;
  }

  const slotButtons = slots.map((slot) => {
    const time = formatTime(slot.start_time);
    const resource = slot.resource_name ? escapeHtml(slot.resource_name) : '';
    return `
      <button class="ts-slot-btn" role="option" aria-selected="false"
        data-action="select-slot" data-slot='${JSON.stringify(slot).replace(/'/g, '&#39;')}'
        aria-label="${time}${resource ? ` with ${resource}` : ''}">
        <span class="ts-slot-btn__time">${time}</span>
        ${resource ? `<span class="ts-slot-btn__resource">${resource}</span>` : ''}
      </button>`;
  }).join('');

  return `
    <div class="ts-slot-grid" role="listbox" aria-label="Available time slots">
      ${slotButtons}
    </div>`;
}

// ── Main Render ───────────────────────────────────────────────────────

export async function renderReschedulePage(params: RescheduleParams): Promise<void> {
  const appEl = document.getElementById('app');
  if (!appEl) return;

  const { bookingId, token } = params;

  // Loading state
  appEl.innerHTML = `
    <div class="ts-page ts-page--reschedule">
      <h1 class="ts-page__title">Reschedule Booking</h1>
      <div class="ts-loading" aria-label="Loading booking details" role="status">
        <div class="ts-loading__spinner"></div>
        <p class="ts-loading__text">Loading your booking details...</p>
      </div>
    </div>`;

  let booking: BookingStatus;
  try {
    booking = await fetchBookingForReschedule(bookingId, token);
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Could not load booking details.';
    appEl.innerHTML = `
      <div class="ts-page ts-page--reschedule">
        <h1 class="ts-page__title">Reschedule Booking</h1>
        <div class="ts-error" role="alert">
          <p class="ts-error__message">${escapeHtml(message)}</p>
          <p class="ts-error__hint">Please check the link in your confirmation email or contact us for help.</p>
        </div>
      </div>`;
    return;
  }

  // Initial state
  const today = new Date();
  currentWeekStart = new Date(today);
  selectedDate = today.toISOString().slice(0, 10);

  await loadAndRenderSlots(appEl, booking, bookingId, token);
}

async function loadAndRenderSlots(
  appEl: HTMLElement,
  booking: BookingStatus,
  bookingId: string,
  token: string,
): Promise<void> {
  const weekDates = getWeekDates(currentWeekStart);
  const dateFrom = weekDates[0]!;
  const dateTo = weekDates[6]!;

  // Extract service_id from booking — assume it is exposed, otherwise search all
  try {
    availability = await searchAvailability({
      service_id: booking.service_name, // May need adaptation
      date_from: dateFrom,
      date_to: dateTo,
    });
  } catch {
    availability = [];
  }

  renderFullReschedulePage(appEl, booking, bookingId, token, weekDates);
}

function renderFullReschedulePage(
  appEl: HTMLElement,
  booking: BookingStatus,
  bookingId: string,
  token: string,
  weekDates: string[],
): void {
  const slotsForDate = availability.find((d) => d.date === selectedDate)?.slots ?? [];
  const slotGrid = renderSlotGrid(slotsForDate);
  const dateSelector = renderDateSelector(weekDates);

  appEl.innerHTML = `
    <div class="ts-page ts-page--reschedule">
      <h1 class="ts-page__title">Reschedule Booking</h1>

      <div class="ts-reschedule-current" role="region" aria-label="Current booking">
        <h2 class="ts-reschedule-current__heading">Current Booking</h2>
        <dl class="ts-reschedule-current__details">
          <div class="ts-reschedule-current__row">
            <dt>Service</dt>
            <dd></dd>
          </div>
          <div class="ts-reschedule-current__row">
            <dt>Date & Time</dt>
            <dd>${escapeHtml(formatDateTime(booking.start_time))}</dd>
          </div>
          ${booking.resource_name ? `<div class="ts-reschedule-current__row"><dt>With</dt><dd>${escapeHtml(booking.resource_name)}</dd></div>` : ''}
        </dl>
      </div>

      <h2 class="ts-reschedule__pick-heading">Choose a new time</h2>
      ${dateSelector}
      ${selectedDate ? `<p class="ts-page__subtitle">${formatDate(selectedDate)}</p>` : ''}
      <div id="slot-container">${slotGrid}</div>
    </div>`;

  // Set service name safely
  const serviceDD = appEl.querySelector('.ts-reschedule-current__details dd');
  if (serviceDD) serviceDD.textContent = booking.service_name;

  attachRescheduleListeners(appEl, booking, bookingId, token);
}

function attachRescheduleListeners(
  appEl: HTMLElement,
  booking: BookingStatus,
  bookingId: string,
  token: string,
): void {
  // Date selection
  const dateBtns = appEl.querySelectorAll('[data-action="select-date"]');
  for (const btn of dateBtns) {
    btn.addEventListener('click', (e) => {
      const target = e.currentTarget as HTMLElement;
      const date = target.dataset['date'];
      if (date) {
        selectedDate = date;
        loadAndRenderSlots(appEl, booking, bookingId, token);
      }
    });
  }

  // Week navigation
  appEl.querySelector('[data-action="prev-week"]')?.addEventListener('click', () => {
    currentWeekStart.setDate(currentWeekStart.getDate() - 7);
    loadAndRenderSlots(appEl, booking, bookingId, token);
  });
  appEl.querySelector('[data-action="next-week"]')?.addEventListener('click', () => {
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    loadAndRenderSlots(appEl, booking, bookingId, token);
  });

  // Slot selection
  const slotBtns = appEl.querySelectorAll('[data-action="select-slot"]');
  for (const btn of slotBtns) {
    btn.addEventListener('click', async (e) => {
      const target = e.currentTarget as HTMLElement;
      const slotData = target.dataset['slot'];
      if (!slotData) return;

      try {
        const slot = JSON.parse(slotData) as AvailableSlot;
        const confirmed = window.confirm(
          `Reschedule to ${formatTime(slot.start_time)} on ${selectedDate}?`,
        );
        if (!confirmed) return;

        // Disable all slot buttons
        for (const b of slotBtns) {
          (b as HTMLButtonElement).disabled = true;
        }

        await submitReschedule(bookingId, token, slot.start_time);

        // Show confirmation
        appEl.innerHTML = `
          <div class="ts-page ts-page--reschedule">
            <div class="ts-confirmation" role="status">
              <svg class="ts-confirmation__icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--ts-primary, #1B4D5C)" stroke-width="2" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/><polyline points="8 12 11 15 16 9"/>
              </svg>
              <h1 class="ts-confirmation__title">Booking Rescheduled</h1>
              <p class="ts-confirmation__message">
                Your booking has been moved to <strong>${escapeHtml(formatTime(slot.start_time))}</strong> on <strong>${selectedDate}</strong>.
              </p>
              <p class="ts-confirmation__hint">You will receive a confirmation email shortly.</p>
            </div>
          </div>`;
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Reschedule failed. Please try again.';
        const errorDiv = document.createElement('div');
        errorDiv.className = 'ts-error';
        errorDiv.setAttribute('role', 'alert');
        errorDiv.innerHTML = `<p class="ts-error__message">${escapeHtml(msg)}</p>`;
        const slotContainer = appEl.querySelector('#slot-container');
        if (slotContainer) slotContainer.prepend(errorDiv);
        for (const b of slotBtns) {
          (b as HTMLButtonElement).disabled = false;
        }
      }
    });
  }
}
